import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import * as Icons from 'react-icons/fa';

const FaMapMarkerAlt = Icons.FaMapMarkerAlt as any;
const FaSpinner = Icons.FaSpinner as any;
const FaMap = Icons.FaMap as any;
const FaExternalLinkAlt = Icons.FaExternalLinkAlt as any;
const FaTimes = Icons.FaTimes as any;

interface MapLocationPickerProps {
  value: string; // Format: "latitude, longitude"
  onChange: (value: string, latitude: number, longitude: number) => void;
  disabled?: boolean;
  hasError?: boolean;
  enableMapSelection?: boolean; // Flag to enable/disable map selection
}

const Container = styled.div`
  width: 100%;
`;

const LocationWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LocationInput = styled.input<{ hasError?: boolean }>`
  flex: 1;
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.hasError ? props.theme.error : props.theme.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  &:disabled {
    background: ${props => props.theme.surface};
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const LocationButton = styled.button<{ hasLocation?: boolean; isCapturing?: boolean; variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${props => 
    props.variant === 'secondary' ? props.theme.border : 
    props.hasLocation ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  background: ${props => 
    props.variant === 'secondary' ? props.theme.surface :
    props.hasLocation ? props.theme.primary + '10' : 'white'};
  color: ${props => 
    props.variant === 'secondary' ? props.theme.textPrimary :
    props.hasLocation ? props.theme.primary : props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.3s;
  justify-content: center;
  font-weight: 500;
  font-size: 0.9rem;
  white-space: nowrap;
  flex: 1;
  min-width: 120px;

  svg {
    font-size: 1rem;
  }

  &:hover:not(:disabled) {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MapContainer = styled.div<{ isVisible: boolean }>`
  display: ${props => props.isVisible ? 'block' : 'none'};
  width: 100%;
  height: 400px;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  margin-top: 0.5rem;
  position: relative;
  background: ${props => props.theme.surface};
  overflow: hidden;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
  text-align: center;
  padding: 2rem;
`;

const MapIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const SingleInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SingleLocationInput = styled.input<{ hasError?: boolean }>`
  flex: 1;
  width: 100%;
  padding: 0.75rem 3.5rem 0.75rem 0.75rem;
  border: 1px solid ${props => props.hasError ? props.theme.error : props.theme.border};
  border-radius: 8px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  &:disabled {
    background: ${props => props.theme.surface};
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: ${props => props.theme.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primary}10;
    color: ${props => props.theme.primaryDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1rem;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 95%;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.textPrimary};
  font-size: 1.2rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.surface};
    color: ${props => props.theme.textPrimary};
  }

  svg {
    font-size: 1.2rem;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InstructionText = styled.p`
  margin: 0;
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const CoordinateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: ${props => props.theme.textPrimary};
  font-weight: 500;
`;

const CoordinateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  @media (max-width: 480px) {
    padding: 0.65rem;
    font-size: 0.85rem;
  }
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryDark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  enableMapSelection = true
}) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLat, setModalLat] = useState('');
  const [modalLng, setModalLng] = useState('');

  // Parse value to get coordinates
  useEffect(() => {
    if (value) {
      const parts = value.split(',').map(p => parseFloat(p.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        setCoordinates({ lat: parts[0], lng: parts[1] });
      }
    }
  }, [value]);

  const handleGPSCapture = () => {
    if (navigator.geolocation) {
      setIsCapturing(true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          // Limit to 5 decimal places
          const lat = parseFloat(latitude.toFixed(5));
          const lng = parseFloat(longitude.toFixed(5));
          const locationString = `${lat}, ${lng}`;
          onChange(locationString, lat, lng);
          setCoordinates({ lat, lng });
          setIsCapturing(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please check your browser permissions.');
          setIsCapturing(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      alert('Geolocation is not supported by this browser');
    }
  };

  const openMapModal = () => {
    if (coordinates) {
      setModalLat(coordinates.lat.toString());
      setModalLng(coordinates.lng.toString());
    } else {
      setModalLat('');
      setModalLng('');
    }
    setShowModal(true);
  };

  const closeMapModal = () => {
    setShowModal(false);
  };

  const handleModalApply = () => {
    const lat = parseFloat(parseFloat(modalLat).toFixed(5));
    const lng = parseFloat(parseFloat(modalLng).toFixed(5));

    if (!isNaN(lat) && !isNaN(lng)) {
      const locationString = `${lat}, ${lng}`;
      onChange(locationString, lat, lng);
      setCoordinates({ lat, lng });
      setShowMap(true);
      closeMapModal();
    } else {
      alert('Please enter valid latitude and longitude values');
    }
  };

  const openGoogleMaps = () => {
    const lat = parseFloat(modalLat) || coordinates?.lat || 14.5995;
    const lng = parseFloat(modalLng) || coordinates?.lng || 120.9842;
    window.open(`https://www.google.com/maps/@${lat},${lng},15z`, '_blank');
  };

  const handleMapSelection = () => {
    setShowMap(!showMap);
  };

  const getMapUrl = () => {
    if (coordinates) {
      // Google Maps with marker
      return `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}&output=embed`;
    }
    // Default center (Philippines)
    return `https://www.google.com/maps?q=14.5995,120.9842&output=embed`;
  };

  return (
    <Container>
      <SingleInputWrapper>
        <SingleLocationInput
          type="text"
          value={value}
          readOnly
          placeholder="GPS coordinates will appear here"
          disabled={disabled || isCapturing}
          hasError={hasError}
        />
        <IconButton
          type="button"
          onClick={handleGPSCapture}
          disabled={disabled || isCapturing}
          title="Capture GPS Location"
        >
          {isCapturing ? <FaSpinner className="fa-spin" /> : <FaMapMarkerAlt />}
        </IconButton>
        {enableMapSelection && (
          <IconButton
            type="button"
            onClick={openMapModal}
            disabled={disabled}
            title="Select on Map"
            style={{ right: '3rem' }}
          >
            <FaMap />
          </IconButton>
        )}
      </SingleInputWrapper>

      <ButtonGroup>
        <LocationButton
          type="button"
          onClick={handleMapSelection}
          disabled={disabled}
          variant="secondary"
        >
          <FaMap />
          {showMap ? 'Hide Map' : 'View Map'}
        </LocationButton>
      </ButtonGroup>

      {enableMapSelection && showMap && (
        <MapContainer isVisible={showMap}>
          {coordinates ? (
            <MapIframe
              src={getMapUrl()}
              title="Location Map"
              loading="lazy"
            />
          ) : (
            <MapPlaceholder>
              <FaMap style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }} />
              <p>Capture GPS location first to view on map</p>
              <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                Click "Capture GPS" to get your current location
              </p>
            </MapPlaceholder>
          )}
        </MapContainer>
      )}

      {showModal && (
        <ModalOverlay onClick={closeMapModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Select Location on Map</ModalTitle>
              <CloseButton onClick={closeMapModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <InstructionText>
                Enter coordinates manually or use Google Maps to find a location:
              </InstructionText>
              
              <CoordinateGrid>
                <InputGroup>
                  <Label>Latitude</Label>
                  <CoordinateInput
                    type="number"
                    step="any"
                    value={modalLat}
                    onChange={(e) => setModalLat(e.target.value)}
                    placeholder="e.g., 14.5995"
                  />
                </InputGroup>
                <InputGroup>
                  <Label>Longitude</Label>
                  <CoordinateInput
                    type="number"
                    step="any"
                    value={modalLng}
                    onChange={(e) => setModalLng(e.target.value)}
                    placeholder="e.g., 120.9842"
                  />
                </InputGroup>
              </CoordinateGrid>
              
              <ModalButton onClick={openGoogleMaps}>
                <FaExternalLinkAlt />
                Open Google Maps
              </ModalButton>
              
              <ModalButton onClick={handleModalApply} disabled={!modalLat || !modalLng}>
                Apply Coordinates
              </ModalButton>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default MapLocationPicker;

